import { AddIcon } from '@chakra-ui/icons';
import {
    Box,
    chakra,
    HStack,
    Icon,
    IconButton,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaSortAmountDown, FaSortAmountUpAlt } from 'react-icons/fa';
import { TbTrash } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import { AutoSizer, List } from 'react-virtualized';
import 'react-virtualized/styles.css';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Card,
    CardBody,
} from '@/components/v4';
import {
    ERROR,
    initTableState,
} from 'pages/telephony/numberBlocks/add/constants';
import UploadMethodSelector from '../components/UploadMethodSelector';
import { findObjectsWithSameNumber } from 'utils/numberBlockUtils.js';
import getRuntimeAppConfig from 'utils/runtimeAppConfig';
import {
    EditableBlockSizeCell,
    EditableNumberCell,
    LastNumberCell,
} from './EditableCells';
import UsageTypeField from '../formFields/UsageTypeField';

const config = getRuntimeAppConfig();

function AddBlockTable({
    aggregateTable,
    updateAggregateTable,
    hasError,
    setHasError,
    setHasEmptyNumber,
    companyIsTrial,
    uploadMethod,
    setUploadMethod,
    isCreateOrderWholesaler,
    usage,
    setFieldValue,
    handleFieldValueChange,
    countryMismatchRows,
}) {
    const [sorting, setSorting] = useState([]);
    const [focusedRow, setFocusedRow] = useState({ row: -1, column: null });
    const [copy, setCopy] = useState(false);
    const [duplicates, setDuplicates] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const isAddingRowRef = useRef(false);
    const columnHelper = createColumnHelper();
    const { darkMode } = useSelector((state) => state.settings);
    const isTableDisabled = isCreateOrderWholesaler && !usage;

    const columns = [
        columnHelper.accessor('blockSize', {
            header: 'Block Size',
            cell: EditableBlockSizeCell,
            size: 100,
            enableSorting: true,
        }),
        columnHelper.accessor('number', {
            header: () => (
                <Tooltip
                    label="To quickly add multiple numbers of the same block size, copy and paste a line separated list of numbers into the First Number field below to create new rows automatically"
                    hasArrow
                    placement="top">
                    <span>First Number</span>
                </Tooltip>
            ),
            cell: EditableNumberCell,
            size: 180,
            enableSorting: false,
        }),
        columnHelper.accessor('lastNumber', {
            header: 'Last Number',
            cell: LastNumberCell,
            size: 180,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            size: 100,
            cell: ({ row, table }) => {
                const rowCount = table.options.data.length;
                const hasNumber = Boolean(row.original.number);
                const isFirstRow = row.original.id === 0;
                const isFocused = focusedRow.row === row.original.id;
                const showDelete = rowCount > 1;
                const showAdd =
                    hasNumber && isFirstRow && isFocused && !isTableDisabled;

                // If no icons to show, render placeholder for spacing
                if (!showDelete && !showAdd) {
                    return <Box w="66px" />;
                }

                return (
                    <Box display="flex" gap={1} alignItems="center">
                        {/* Delete button - hidden when only 1 row */}
                        {showDelete ? (
                            <Tooltip label="Delete row" hasArrow>
                                <IconButton
                                    size="sm"
                                    rounded="full"
                                    variant="ghost"
                                    color={darkMode ? 'white' : 'gray.500'}
                                    icon={<TbTrash />}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        const newIndex =
                                            row.index > 0 ? row.index - 1 : 0;
                                        table.options.meta.deleteRow(row.index);
                                        table.options.meta.setFocusedRow(
                                            newIndex,
                                            'number',
                                        );
                                    }}
                                    _hover={{
                                        bg: darkMode
                                            ? 'dark.hoverGray'
                                            : 'gray.100',
                                        color: 'red.500',
                                    }}
                                    _focus={{
                                        boxShadow: 'none',
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Box w="32px" aria-label="action placeholder" />
                        )}

                        {/* Add button - visible when row has a number */}
                        {showAdd ? (
                            <Tooltip label="Add new row" hasArrow>
                                <IconButton
                                    size="sm"
                                    rounded="full"
                                    variant="ghost"
                                    icon={
                                        <AddIcon
                                            color={
                                                darkMode ? 'white' : 'gray.500'
                                            }
                                            boxSize="12px"
                                            className="h-5 w-5"
                                        />
                                    }
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        table.options.meta.updateData(
                                            row.index,
                                            'number',
                                            row.original.number,
                                            true,
                                        );
                                        table.options.meta.setFocusedRow(
                                            0,
                                            'number',
                                        );
                                    }}
                                    _hover={{
                                        bg: darkMode
                                            ? 'dark.hoverGray'
                                            : 'gray.100',
                                        color: 'brand.500',
                                    }}
                                    _focus={{
                                        boxShadow: 'none',
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Box w="32px" aria-label="action placeholder" />
                        )}
                    </Box>
                );
            },
        }),
    ];

    // Handle duplicate detection
    const findObjWithSameNumber = useCallback(findObjectsWithSameNumber, []);

    // Debounced validation. Previously duplicates, warnings and the empty-row
    // check each re-ran reactively on every row change, re-painting the whole
    // table per row (effectively O(n²) on large copy/pastes). Coalesce them
    // into a single pass that runs ~300ms after the user stops editing, so a
    // bulk paste validates once after it settles. A synchronous guard in
    // handleModalSubmit covers a submit fired inside this window.
    useEffect(() => {
        const handle = setTimeout(() => {
            // Duplicates
            const foundDuplicates = findObjWithSameNumber(
                aggregateTable,
            ).filter((r) => Boolean(r?.number));
            setDuplicates(foundDuplicates);

            // Zero / short-number warnings
            const found = [];
            aggregateTable?.forEach((row) => {
                if (!row.number) return;
                const digits = row.number.replace(/\D/g, '');
                if (digits.startsWith('0')) {
                    found.push({
                        id: row.id,
                        number: row.number,
                        type: ERROR.ZERO.type,
                    });
                } else if (digits.length > 0 && digits.length <= 5) {
                    found.push({
                        id: row.id,
                        number: row.number,
                        type: ERROR.SHORT.type,
                    });
                }
            });
            setWarnings(found);

            // Empty rows
            setHasEmptyNumber(aggregateTable?.some((row) => row.number === ''));

            // Submit gate: duplicates or zero-prefixed numbers block submit
            const hasWarnings = found.some((w) => w.type === ERROR.ZERO.type);
            const hasDuplicate =
                foundDuplicates.filter((number) => number.number !== '')
                    .length > 0;
            setHasError(hasDuplicate || hasWarnings);
        }, 300);

        return () => clearTimeout(handle);
    }, [aggregateTable, findObjWithSameNumber, setHasError, setHasEmptyNumber]);

    // Handle paste event for multi-number input
    useEffect(() => {
        const handlePaste = (event) => {
            if (event.target.tagName === 'TEXTAREA') {
                return;
            }

            if (isTableDisabled) {
                return;
            }

            // Get clipboard data
            const clipboardData = event.clipboardData || window.clipboardData;
            const pastedText = clipboardData.getData('text');

            // Split by newlines only (not commas - commas may be formatting within a number)
            const numbers = pastedText
                .split(/[\n\r]+/)
                .map((num) => num.replace(/[^\d]/g, '').slice(0, 19))
                .filter((num) => num.length > 0);

            // Only process if we have multiple numbers
            if (numbers.length > 1) {
                event.preventDefault();

                // Get the default block size from the first row or use 1
                const defaultBlockSize = isCreateOrderWholesaler
                    ? 1
                    : aggregateTable[0]?.blockSize || 1;

                // Create new rows for each number
                const newRows = numbers.map((number) => ({
                    blockSize: defaultBlockSize,
                    number: `+${number}`,
                    countryCode: '',
                }));

                // Append to existing rows (keep rows that have a number)
                updateAggregateTable((prevTable) => {
                    const existingRows = prevTable.filter(
                        (row) => row.number !== '',
                    );

                    // Put new pasted rows FIRST, then existing rows
                    return [...newRows, ...existingRows].map((row, index) => ({
                        ...row,
                        id: index,
                    }));
                });
            }
        };

        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [
        aggregateTable,
        isCreateOrderWholesaler,
        isTableDisabled,
        updateAggregateTable,
    ]);

    const table = useReactTable({
        columns,
        data: aggregateTable,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
        },
        meta: {
            focusedRow,
            setFocusedRow: (rowIndex, columnId) => {
                setFocusedRow({ row: rowIndex, column: columnId });
            },
            copy,
            setCopy,
            duplicates,
            hasError,
            isAddingRowRef,
            warnings,
            companyIsTrial,
            countryMismatchRows,
            isCreateOrderWholesaler,
            isTableDisabled,
            updateData: (rowIndex, columnId, value, addRow = false) => {
                if (addRow) {
                    isAddingRowRef.current = true;
                    setTimeout(() => {
                        isAddingRowRef.current = false;
                    }, 0);
                }
                updateAggregateTable((prevTable) => {
                    let newTable = [...prevTable];
                    const nextValue =
                        isCreateOrderWholesaler && columnId === 'blockSize'
                            ? 1
                            : value;
                    newTable[rowIndex] = {
                        ...newTable[rowIndex],
                        [columnId]: nextValue,
                    };

                    if (addRow) {
                        // Inherit block size from the row that clicked add
                        const inheritedBlockSize = isCreateOrderWholesaler
                            ? 1
                            : newTable[rowIndex]?.blockSize || 1;
                        // Add new row at the top
                        newTable.unshift({
                            id: 0,
                            blockSize: inheritedBlockSize,
                            number: '',
                            countryCode: '',
                        });

                        // Re-index all rows
                        newTable = newTable.map((row, idx) => ({
                            ...row,
                            id: idx,
                        }));
                    }

                    return newTable;
                });
            },
            deleteRow: (rowIndex) => {
                updateAggregateTable((prevTable) => {
                    const newTable = prevTable.filter(
                        (_, index) => index !== rowIndex,
                    );
                    // Ensure we always have at least one row
                    if (newTable.length === 0) {
                        return initTableState;
                    }
                    // Re-index the remaining rows
                    return newTable.map((row, index) => ({
                        ...row,
                        id: index,
                    }));
                });
            },
            handleBulkPaste: (numbers, defaultBlockSize) => {
                updateAggregateTable((prevTable) => {
                    // Keep existing rows that have a number value
                    const existingRows = prevTable.filter(
                        (row) => row.number !== '',
                    );
                    const newRows = numbers.map((number) => ({
                        id: 0,
                        blockSize: isCreateOrderWholesaler
                            ? 1
                            : defaultBlockSize,
                        number: `+${number}`,
                        countryCode: '',
                    }));
                    // Put new pasted rows FIRST, then existing rows
                    const combined = [...newRows, ...existingRows];
                    // Re-index all rows
                    return combined.map((row, index) => ({
                        ...row,
                        id: index,
                    }));
                });
            },
            clearAllRows: () => {
                updateAggregateTable(() => initTableState);
                setFocusedRow({ row: 0, column: 'blockSize' });
            },
        },
    });

    // Virtualize only large lists (the 1000+ row paste case). Small forms keep
    // the original semantic table untouched. Rows mirror table sort order.
    const VIRTUALIZE_THRESHOLD = 100;
    const ROW_HEIGHT = 44;
    const LIST_HEIGHT = 400;
    const tableRows = table.getRowModel().rows;
    const useVirtual = tableRows.length > VIRTUALIZE_THRESHOLD;

    // Shared column layout so the virtualized header and rows line up: the two
    // number columns flex, block size and actions stay fixed.
    const getColLayout = (colId) =>
        colId === 'number' || colId === 'lastNumber'
            ? { flex: '1 1 0%', minWidth: '180px' }
            : { width: '100px', flexShrink: 0 };

    return (
        <Box>
            {/* Header with Clear All and CSV Update buttons */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                maxW="895px">
                <Text
                    fontWeight="semibold"
                    fontSize="lg"
                    color={darkMode ? 'white' : 'gray.800'}>
                    Number Block Table
                </Text>
                <HStack spacing={2} justifyContent="flex-start" minW="84px">
                    {/* Clear All button - only visible when > 1 row */}
                    {aggregateTable?.length > 1 && (
                        <Tooltip label="Clear all rows" hasArrow>
                            <IconButton
                                size="sm"
                                rounded="full"
                                icon={<TbTrash />}
                                variant="ghost"
                                color={darkMode ? 'white' : 'gray.500'}
                                onClick={() =>
                                    table.options.meta.clearAllRows()
                                }
                                _hover={{
                                    bg: darkMode
                                        ? 'dark.hoverGray'
                                        : 'gray.100',
                                    color: 'red.500',
                                }}
                            />
                        </Tooltip>
                    )}
                    <Box w="32px" aria-label="action placeholder" />
                </HStack>
            </Box>

            <Alert
                status={countryMismatchRows?.length > 0 ? 'error' : 'info'}
                display="-webkit-box"
                my={4}>
                <AlertIcon />
                <AlertDescription fontSize="sm">
                    {countryMismatchRows?.length > 0 ? (
                        <Text>
                            Detected different country calling codes in the
                            table. All numbers must share the same country
                            calling code.
                        </Text>
                    ) : (
                        <>
                            <Text>
                                To quickly add multiple numbers of the same
                                block size, copy and paste a
                                <span
                                    style={{
                                        marginInline: '3px',
                                        fontWeight: 'bold',
                                    }}>
                                    line separated list
                                </span>
                                of numbers{' '}
                            </Text>
                            <Text>
                                into the
                                <span
                                    style={{
                                        marginInline: '3px',
                                        fontWeight: 'bold',
                                    }}>
                                    First Number
                                </span>
                                field below. All configured rows must share the
                                same country calling code.
                            </Text>
                        </>
                    )}
                </AlertDescription>
            </Alert>

            {isCreateOrderWholesaler && (
                <UsageTypeField
                    darkMode={darkMode}
                    value={usage}
                    setFieldValue={setFieldValue}
                    handleFieldValueChange={handleFieldValueChange}
                />
            )}

            {!isCreateOrderWholesaler &&
                aggregateTable.length >= (config.minBlocksForOrder ?? 5) && (
                    <UploadMethodSelector
                        uploadMethod={uploadMethod}
                        setUploadMethod={setUploadMethod}
                        darkMode={darkMode}
                    />
                )}

            <Card
                bg={darkMode ? 'dark.tcap' : '#FFF'}
                maxW="895px"
                shadow="none">
                <CardBody p={0}>
                    {useVirtual ? (
                        <Box>
                            {/* Virtualized header (div grid mirroring the table) */}
                            <Box
                                display="flex"
                                alignItems="center"
                                height="48px"
                                px={0.5}
                                position="sticky"
                                top={0}
                                zIndex={10}
                                background={darkMode ? 'dark.tcap' : '#FFF'}>
                                {table
                                    .getHeaderGroups()[0]
                                    .headers.map((header) => (
                                        <Box
                                            key={header.id}
                                            display="flex"
                                            alignItems="center"
                                            fontSize="sm"
                                            fontWeight="semibold"
                                            letterSpacing="wide"
                                            textTransform="capitalize"
                                            color={
                                                darkMode ? 'white' : 'gray.600'
                                            }
                                            {...getColLayout(header.column.id)}>
                                            <chakra.span
                                                onClick={header.column.getToggleSortingHandler()}
                                                className={
                                                    header.column.getCanSort()
                                                        ? 'cursor-pointer'
                                                        : ''
                                                }>
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                            </chakra.span>
                                            {header.column.getCanSort() &&
                                                header.column.getIsSorted() && (
                                                    <chakra.span
                                                        pl="2"
                                                        onClick={header.column.getToggleSortingHandler()}
                                                        className="cursor-pointer">
                                                        {header.column.getIsSorted() ===
                                                        'desc' ? (
                                                            <Icon
                                                                as={
                                                                    FaSortAmountDown
                                                                }
                                                                aria-label="sorted descending"
                                                            />
                                                        ) : (
                                                            <Icon
                                                                as={
                                                                    FaSortAmountUpAlt
                                                                }
                                                                aria-label="sorted ascending"
                                                            />
                                                        )}
                                                    </chakra.span>
                                                )}
                                        </Box>
                                    ))}
                            </Box>

                            {/* Windowed rows */}
                            <Box height={`${LIST_HEIGHT}px`}>
                                <AutoSizer disableHeight>
                                    {({ width }) => (
                                        <List
                                            width={width || 1}
                                            height={LIST_HEIGHT}
                                            rowCount={tableRows.length}
                                            rowHeight={ROW_HEIGHT}
                                            overscanRowCount={10}
                                            // Pass-through props so the Grid
                                            // re-renders rows when validation
                                            // highlights / focus change.
                                            duplicates={duplicates}
                                            warnings={warnings}
                                            countryMismatchRows={
                                                countryMismatchRows
                                            }
                                            focusedRow={focusedRow}
                                            aggregateTable={aggregateTable}
                                            darkMode={darkMode}
                                            rowRenderer={({
                                                index,
                                                key,
                                                style,
                                            }) => {
                                                const row = tableRows[index];
                                                return (
                                                    <Box
                                                        key={key}
                                                        style={style}
                                                        display="flex"
                                                        alignItems="center"
                                                        px={0.5}
                                                        bg={
                                                            darkMode
                                                                ? 'dark.tcap'
                                                                : '#FFF'
                                                        }>
                                                        {row
                                                            .getVisibleCells()
                                                            .map((cell) => (
                                                                <Box
                                                                    key={
                                                                        cell.id
                                                                    }
                                                                    id={cell.id}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    pr={2}
                                                                    {...getColLayout(
                                                                        cell
                                                                            .column
                                                                            .id,
                                                                    )}>
                                                                    {flexRender(
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .cell,
                                                                        cell.getContext(),
                                                                    )}
                                                                </Box>
                                                            ))}
                                                    </Box>
                                                );
                                            }}
                                        />
                                    )}
                                </AutoSizer>
                            </Box>
                        </Box>
                    ) : (
                        <TableContainer
                            width="full"
                            maxW="full"
                            maxHeight="400px"
                            overflowY="auto"
                            sx={{
                                '&': {
                                    scrollbarWidth: 'thin',
                                },
                            }}>
                            <Table size="sm" variant="unstyled">
                                <Thead
                                    position="sticky"
                                    top={0}
                                    zIndex={10}
                                    height="48px"
                                    background={
                                        darkMode ? 'dark.tcap' : '#FFF'
                                    }>
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <Tr
                                                key={headerGroup.id}
                                                sx={{
                                                    '& th': {
                                                        backgroundColor:
                                                            darkMode
                                                                ? 'var(--chakra-colors-dark-tcap)!important'
                                                                : '#FFF!important',
                                                    },
                                                }}>
                                                {headerGroup.headers.map(
                                                    (header, headerIndex) => {
                                                        const isFirst =
                                                            headerIndex === 0;
                                                        const isLast =
                                                            headerIndex ===
                                                            headerGroup.headers
                                                                .length -
                                                                1;
                                                        return (
                                                            <Th
                                                                key={header.id}
                                                                colSpan={
                                                                    header.colSpan
                                                                }
                                                                className="relative"
                                                                textTransform="capitalize"
                                                                fontSize="sm"
                                                                letterSpacing="wide"
                                                                paddingTop="8px"
                                                                paddingBottom="8px"
                                                                {...(isFirst && {
                                                                    pl: 0,
                                                                })}
                                                                {...(isLast && {
                                                                    pr: 0,
                                                                })}>
                                                                <span
                                                                    onClick={header.column.getToggleSortingHandler()}
                                                                    className={
                                                                        header.column.getCanSort()
                                                                            ? 'cursor-pointer'
                                                                            : ''
                                                                    }>
                                                                    {flexRender(
                                                                        header
                                                                            .column
                                                                            .columnDef
                                                                            .header,
                                                                        header.getContext(),
                                                                    )}
                                                                </span>
                                                                {header.column.getCanSort() && (
                                                                    <chakra.span
                                                                        pl="2"
                                                                        onClick={header.column.getToggleSortingHandler()}
                                                                        className="cursor-pointer">
                                                                        {header.column.getIsSorted() ? (
                                                                            header.column.getIsSorted() ===
                                                                            'desc' ? (
                                                                                <Icon
                                                                                    as={
                                                                                        FaSortAmountDown
                                                                                    }
                                                                                    aria-label="sorted descending"
                                                                                />
                                                                            ) : (
                                                                                <Icon
                                                                                    as={
                                                                                        FaSortAmountUpAlt
                                                                                    }
                                                                                    aria-label="sorted ascending"
                                                                                />
                                                                            )
                                                                        ) : null}
                                                                    </chakra.span>
                                                                )}
                                                            </Th>
                                                        );
                                                    },
                                                )}
                                            </Tr>
                                        ))}
                                </Thead>

                                <Tbody>
                                    {aggregateTable?.length > 0 ? (
                                        table.getRowModel().rows.map((row) => (
                                            <Tr
                                                key={row.id}
                                                bg={
                                                    darkMode
                                                        ? 'dark.tcap'
                                                        : '#FFF'
                                                }>
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell, cellIndex) => {
                                                        const isFirst =
                                                            cellIndex === 0;
                                                        const isLast =
                                                            cellIndex ===
                                                            row.getVisibleCells()
                                                                ?.length -
                                                                1;
                                                        return (
                                                            <Td
                                                                key={cell.id}
                                                                id={cell.id}
                                                                sx={{
                                                                    border: 0,
                                                                }}
                                                                {...(isFirst && {
                                                                    pl: 0.5,
                                                                })}
                                                                {...(isLast && {
                                                                    pr: 0.5,
                                                                })}>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="flex-start">
                                                                    {flexRender(
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .cell,
                                                                        cell.getContext(),
                                                                    )}
                                                                </Box>
                                                            </Td>
                                                        );
                                                    })}
                                            </Tr>
                                        ))
                                    ) : (
                                        <Tr>
                                            <Td
                                                colSpan={columns.length}
                                                className="!border-none !bg-transparent text-center">
                                                No data to display
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    )}
                </CardBody>
            </Card>
        </Box>
    );
}

export default AddBlockTable;
